<template>
  <div v-if="sortedAndFilteredAlertList.length">
    <div
      v-for="alert in sortedAndFilteredAlertList"
      :key="alert.id"
      class="alert-card"
    >
      <div class="alert-card__header">
        <div class="header__left">
          <div
            @click="toggleIsShowDetail(alert.id, !alert.isShowDetail)"
            class="icon"
            :class="{ rotate: alert.isShowDetail }"
          >
            <fa icon="chevron-down"></fa>
          </div>
          <div class="title">{{ alert.name }}</div>
        </div>
        <div class="header__right">
          <div class="icon" @click="setOperationModal(alert.id)">
            <fa icon="ellipsis-v"></fa>
          </div>
          <div
            v-if="showOperationModal === alert.id"
            class="operation-modal"
            v-click-outside="closeAlertOperationModal"
          >
            <p @click="editAlert(alert.id)">Edit</p>
            <p
              v-if="alert.status !== alertStatus.SNOOZED"
              @click="snoozeAlert(alert.id)"
            >
              Snooze
            </p>
            <p v-else @click="unSnoozeAlert(alert.id)">Un-Snooze</p>
            <p @click="deleteAlert(alert.id)">Delete</p>
          </div>
        </div>
      </div>
      <div class="alert-card__content">
        <div class="description">
          <span>Status</span>
          <div
            :class="[getStatusClassName(alert.status)]"
            v-html="getAlertStatus(alert)"
          ></div>
        </div>
        <div class="date">
          <div>
            <span>Timestamp</span>
            <br />
            <span class="small">(last trigger)</span>
          </div>
          <span>{{ alert.dateOfLastTrigger | timestampFormatter }}</span>
        </div>
        <div v-if="alert.isShowDetail" class="detail-container">
          <AlertRSDetailContainer
            :alertDataObj="alert"
          ></AlertRSDetailContainer>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="no-data">No data</div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import * as vClickOutside from 'v-click-outside';
import { mapGetters, mapMutations, mapState, mapActions } from 'vuex';
import AlertRSDetailContainer from './AlertRSDetailContainer.vue';
import {
  STATUS,
  ALERT_FILTER_OPTION,
  ALERT_SORT_OPTION
} from '../../../../constants/alertsRoutingStatusConstant';
import Spinner from '../../../ui/Spinner.vue';
import {
  getElementPositionTop,
  getOmnibarHeight,
  getSubheaderHeight
} from '../../../../utils/BOM';

export default {
  name: 'MobileAlertRSCard',
  components: {
    AlertRSDetailContainer,
    Spinner
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    sortType: {
      type: Number,
      required: true
    },
    filterType: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isShowDetail: false,
      showOperationModal: '',
      alertStatus: STATUS
    };
  },
  computed: {
    ...mapState('alertsRoutingStatusStore', [
      'alertNameQuery',
      'expandedAlertId'
    ]),
    ...mapGetters('alertsRoutingStatusStore', [
      'alertsListOfSelectedAccount',
      'selectedAccountId'
    ]),

    alertListOfSelectedAccountFilteredByAlertNameQuery() {
      const query = new RegExp(this.alertNameQuery, 'i');

      return this.alertsListOfSelectedAccount.filter(
        alert => alert.name.search(query) > -1
      );
    },

    sortedAndFilteredAlertList() {
      let list = [];

      switch (this.filterType) {
        case ALERT_FILTER_OPTION.NONE:
          list = this.alertListOfSelectedAccountFilteredByAlertNameQuery;
          break;
        case ALERT_FILTER_OPTION.FIRING:
          list = this.alertListOfSelectedAccountFilteredByAlertNameQuery.filter(
            alert => alert.status === STATUS.FIRING
          );
          break;
        case ALERT_FILTER_OPTION.FIRED:
          list = this.alertListOfSelectedAccountFilteredByAlertNameQuery.filter(
            alert => alert.status === STATUS.FIRED
          );
          break;
        case ALERT_FILTER_OPTION.SNOOZED:
          list = this.alertListOfSelectedAccountFilteredByAlertNameQuery.filter(
            alert => alert.status === STATUS.SNOOZED
          );
          break;
        case ALERT_FILTER_OPTION.CLEAN:
          list = this.alertListOfSelectedAccountFilteredByAlertNameQuery.filter(
            alert => alert.status === STATUS.CLEAN
          );
          break;
        default:
          list = this.alertListOfSelectedAccountFilteredByAlertNameQuery;
          break;
      }

      // deep clone list as list.sort will change the origin list
      list = JSON.parse(JSON.stringify(list));

      switch (this.sortType) {
        case ALERT_SORT_OPTION.NONE:
          break;
        case ALERT_SORT_OPTION.NAME_ASCENDING:
          list.sort((a, b) => {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();

            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;

            return 0;
          });
          break;
        case ALERT_SORT_OPTION.LAST_TRIGGER_NEWEST_FIRST:
          list.sort((a, b) => {
            const lastTriggerDateA = dayjs(a.dateOfLastTrigger || 0).valueOf();
            const lastTriggerDateB = dayjs(b.dateOfLastTrigger || 0).valueOf();

            if (lastTriggerDateA < lastTriggerDateB) return 1;
            if (lastTriggerDateA > lastTriggerDateB) return -1;

            return 0;
          });
          break;
        case ALERT_SORT_OPTION.LAST_TRIGGER_OLDEST_FIRST:
          list.sort((a, b) => {
            const lastTriggerDateA = dayjs(a.dateOfLastTrigger || 0).valueOf();
            const lastTriggerDateB = dayjs(b.dateOfLastTrigger || 0).valueOf();

            if (lastTriggerDateA < lastTriggerDateB) return -1;
            if (lastTriggerDateA > lastTriggerDateB) return 1;

            return 0;
          });
          break;
        default:
          break;
      }

      return list;
    }
  },
  watch: {
    // '$store.state.alertsRoutingStatusStore.expandedAlertId'
    expandedAlertId: {
      immediate: false,
      handler(val) {
        if (val === null || val === undefined) return;

        // Clear filter conditions. Otherwise, the alert may not exist in current table
        this.$emit('onCloseFilterTool');

        // Use setTimeout to allow the alertList to be updated without filter. Otherwise, the alertList is still the filtered one.
        setTimeout(() => {
          const alertIndex = this.sortedAndFilteredAlertList.findIndex(
            item => item.id === val
          );

          if (alertIndex === null || alertIndex === undefined) return;

          const alertCardElementList = document.querySelectorAll('.alert-card');

          if (!alertCardElementList || !alertCardElementList[alertIndex])
            return;

          this.toggleIsShowDetail(val, true);

          // Without setTimeout, scroll behavior cannot be triggered properly in Firefox.
          setTimeout(() => {
            // Get the top position of the last firing alert.
            const alertOffsetTop = getElementPositionTop(
              alertCardElementList[alertIndex]
            );

            // Scroll to the last firing alert.
            const omnibarHeight = getOmnibarHeight();
            const subheaderHeight = getSubheaderHeight();
            window.scrollTo({
              top: alertOffsetTop - omnibarHeight - subheaderHeight,
              left: 0,
              behavior: 'smooth'
            });

            // Reset expandedAlertId in state in case users click on "More details" again.
            this.setExpandedAlertId(null);
          }, 200);
        }, 0);
      }
    }
  },
  methods: {
    ...mapMutations('alertsRoutingStatusStore', [
      'setIsShowDetailOnMobile',
      'setExpandedAlertId'
    ]),
    ...mapActions('alertsRoutingStatusStore', ['fetchHistoryOfSelectedAlert']),

    closeAlertOperationModal() {
      setTimeout(() => {
        this.showOperationModal = '';
      }, 200);
    },
    setOperationModal(val) {
      this.showOperationModal = val;
    },
    editAlert(val) {
      this.$emit('onEditAlert', val);
      this.closeAlertOperationModal();
    },
    snoozeAlert(val) {
      this.$emit('onSnoozeAlert', val);
      this.closeAlertOperationModal();
    },
    unSnoozeAlert(val) {
      this.$emit('onUnSnoozeAlert', val);
      this.closeAlertOperationModal();
    },
    deleteAlert(val) {
      this.$emit('onDeleteAlert', val);
      this.closeAlertOperationModal();
    },
    toggleIsShowDetail(alertId, isShowDetail) {
      this.setIsShowDetailOnMobile({
        selectedAccountId: this.selectedAccountId,
        alertId,
        isShowDetail
      });

      // Fetch history when expand an alert card
      isShowDetail && this.fetchHistoryOfSelectedAlert(alertId);
    },
    getStatusClassName(status) {
      let addedClassName = '';
      switch (status) {
        case STATUS.FIRING:
          addedClassName = 'prefix-icon red-dot';
          break;
        case STATUS.FIRED:
          addedClassName = 'prefix-icon red-circle';
          break;
        case STATUS.SNOOZED:
          addedClassName = 'prefix-icon yellow-dot';
          break;
        case STATUS.CLEAN:
          addedClassName = 'prefix-icon green-dot';
          break;
        default:
          addedClassName = '';
          break;
      }
      return addedClassName;
    },
    getAlertStatus(alert) {
      if (alert.status !== STATUS.SNOOZED) {
        return `<span class='status'>${alert.status}</span>`;
      } else {
        const snoozeToDate = alert.snoozeTo
          ? dayjs(alert.snoozeTo).format('DD MMM YYYY')
          : '';
        return `<span class='status'>${
          alert.status
        }</span><span class='snooze'> (until ${snoozeToDate})</span>`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.alert-card {
  margin-bottom: 1rem;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 3rem;
    background-color: $border-grey;
    width: 100%;

    .header__left {
      display: flex;
      width: calc(100% - 3rem);

      .icon {
        width: 3rem;
        height: 3rem;
        text-align: center;
        line-height: 3rem;
        color: $icon-grey;
        cursor: pointer;
        transition: transform 0.2s;
      }

      .icon.rotate {
        transform: rotateZ(180deg);
      }

      .title {
        line-height: 3rem;
        font-weight: 600;
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }

    .header__right {
      position: relative;
      flex: 1;

      .icon {
        width: 3rem;
        height: 3rem;
        text-align: center;
        line-height: 3rem;
        color: $icon-grey;
        cursor: pointer;
      }

      .operation-modal {
        position: absolute;
        z-index: 9;
        top: 2rem;
        right: 1rem;

        width: 10rem;
        border: 1px #e6e6e6 solid;
        background-color: #fff;
        box-shadow: $alert-card-shadow;

        p {
          //   margin-bottom: 0.5rem;
          margin: 0;
          padding: 0.5rem 1rem;
          cursor: pointer;
        }
        p:nth-child(1) {
          padding-top: 1rem;
        }
        p:nth-child(4) {
          padding-bottom: 1rem;
          color: $warning-red;
        }
        p:hover {
          background-color: rgba($color: #666, $alpha: 0.1);
        }
      }
    }
  }

  &__content {
    background-color: #fff;
    box-shadow: $alert-card-shadow;
    padding: 0 1rem;

    .description,
    .date {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 3rem;

      span:nth-child(1) {
        font-weight: 600;
      }
    }

    .date {
      height: 4rem;
      padding-bottom: 1rem;

      .small {
        font-size: 0.75rem;
        font-weight: 600;
      }
    }

    /deep/ .snooze {
      font-size: 0.8rem;
      font-style: italic;
    }
  }
}

.no-data {
  width: 100%;
  height: 3rem;
  line-height: 3rem;
  background-color: #fff;
  text-align: center;
}
</style>
